import type { PaginateResult } from "./types"

export type CostSpecificationInaccessibleTicket = TicketSubset & {
  asset: Pick<Asset, "_id" | "key" | "description">
  reason: InaccessibleTicketReason
}

export interface GeneratedCostSpecification {
  _id: string
  file: FileData
  totalCosts: number
  invoicePeriod?: string
  routeNumber?: string
  approval?: Approval
}

export enum CostSpecificationStatus {
  NEW = "NEW",
  AWAITING_APPROVAL = "AWAITING_APPROVAL",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  DONE = "DONE",
  CANCELLED = "CANCELLED",
}

export enum InaccessibleTicketReason {
  /** Can be used when the ticket as changed to another supplier */
  NO_ACCESS = "NO_ACCESS",
  /** Can be used when the ticket is deleted */
  NOT_FOUND = "NOT_FOUND",
}

export type PopulatedCostSpecificationTicket = Pick<
  Ticket,
  | "_id"
  | "description"
  | "status"
  | "type"
  | "key"
  | "date"
  | "articles"
  | "contract"
  | "costsLabor"
  | "costsMaterial"
  | "costsTotal"
  | "createdAt"
  | "date"
  | "description"
  | "endAt"
  | "key"
  | "organization"
  | "startAt"
  | "status"
  | "type"
> & {
  asset: Pick<AssetSubset, "_id" | "description" | "key">
}

export interface CostSpecification {
  _id: string
  updatedAt: string
  createdAt: string
  organization: string
  description: string
  remark?: string
  costOrganization: {
    _id: string
    description: string
  }
  contract: {
    _id: string
    description: string
    status: ContractStatus
  }
  routeNumber?: string
  tickets: Array<PopulatedCostSpecificationTicket>
  inaccessibleTickets: Array<CostSpecificationInaccessibleTicket>
  specifications: Array<GeneratedCostSpecification>
  totalCosts?: number
  status: CostSpecificationStatus
}

export interface CostSpecificationCreate {
  status: CostSpecificationStatus
  costOrganization: string
  contract?: string
  routeNumber?: string
  description: string
  remark?: string
  ticketStatus?: TicketStatus
}

export type CostSpecificationUpdate = CostSpecificationCreate

export interface CostSpecificationPdf {
  invoicePeriod?: string
  description: string
  remark?: string
  routeNumber?: string
}

export interface CostSpecificationSummary {
  hasSpecifications: boolean
  hasSpecificationsAwaitingApproval: boolean
  specificationsAwaitingApprovalCount: number
}

export const useCostSpecificationStore = defineStore("cost-specification", () => {
  const _baseUrl = "/cost-specifications"
  const refreshSummaryKey = ref(0)

  const triggerSummaryRefresh = () => {
    refreshSummaryKey.value++
  }

  const getByPage = async (queryParameters: Record<string, unknown> = {}): Promise<PaginateResult<CostSpecification>> => {
    const query = getUrlSearchParams(queryParameters)

    const { data } = await useSamApi<PaginateResult<CostSpecification>>(`${_baseUrl}?${query}`)
    return data
  }

  const getById = async (id: string): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}`)
    return data
  }

  const create = async (body: CostSpecificationCreate): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(_baseUrl, {
      method: "POST",
      body: JSON.stringify(body),
    })

    return data
  }

  const deleteOne = async (id: string): Promise<void> => {
    await useSamApi(`${_baseUrl}/${id}`, {
      method: "DELETE",
    })
  }

  const update = async (id: string, body: CostSpecificationUpdate): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    })

    return data
  }

  const updateTickets = async (id: string, tickets: Array<string>): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}/tickets`, {
      method: "PUT",
      body: JSON.stringify(tickets),
    })

    return data
  }

  const createPdf = async (id: string, body: CostSpecificationPdf): Promise<GeneratedCostSpecification> => {
    const { data } = await useSamApi<GeneratedCostSpecification>(`${_baseUrl}/${id}/create-pdf`, {
      method: "POST",
      body: JSON.stringify(body),
    })

    return data
  }

  const deletePdf = async (id: string, specificationId: string): Promise<GeneratedCostSpecification> => {
    const { data } = await useSamApi<GeneratedCostSpecification>(`${_baseUrl}/${id}/delete-pdf/${specificationId}`, {
      method: "DELETE",
    })

    return data
  }

  const removeRouteNumber = async (id: string, routeNumber: string): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}/remove-route-number`, {
      method: "PUT",
      body: JSON.stringify({ routeNumber }),
    })

    return data
  }

  const approveCostSpecification = async (id: string, specificationId: string): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}/${specificationId}/approve`, {
      method: "PUT",
      body: JSON.stringify({ specificationId }),
    })

    return data
  }

  const denyCostSpecification = async (id: string, specificationId: string, remark?: string): Promise<CostSpecification> => {
    const { data } = await useSamApi<CostSpecification>(`${_baseUrl}/${id}/${specificationId}/deny`, {
      method: "PUT",
      body: JSON.stringify({ remark }),
    })

    return data
  }

  const getSpecificationSummary = async (organizationId: string): Promise<CostSpecificationSummary> => {
    const { data } = await useSamApi<CostSpecificationSummary>(`${_baseUrl}/organization/${organizationId}/summary`)
    return data
  }

  return {
    refreshSummaryKey,
    triggerSummaryRefresh,
    getByPage,
    getById,

    create,
    createPdf,
    deletePdf,
    update,
    deleteOne,
    updateTickets,
    removeRouteNumber,
    approveCostSpecification,
    denyCostSpecification,
    getSpecificationSummary,
  }
})
